// Клиент-сайд NPC (цель-манекен).
//
// Состояние NPC теперь authoritative на сервере:
//   - позиция/yaw/HP/state приходят в Snapshot.npcs;
//   - попадания игрока в NPC считает сервер (в sim.fire хитскан по NPC);
//   - смерть (die/dieBack) сервер сигнализирует через state=1/2 в snapshot;
//   - выстрелы NPC приходят как EvNpcShot (tracer + урон victim'у);
//   - flinch на NPC трилгерится клиентом по детекту падения hp в snapshot'е
//     (как у игроков — см. client/src/main.ts).
//
// Класс Npc хранит только визуальное состояние: MeshInstance, последний
// viewable hp (для flinch-delta), yaw (копия server'ной для хит-детекции
// стороны удара — не используется, т.к. сторона теперь считается на сервере).
// AI ЗДЕСЬ НЕТ — сервер управляет.

import type { MeshInstance, Renderer } from "./renderer";
import type { LoadedMesh } from "./meshLoader";
import type { AnimState } from "./animation";
import { setBaseAnim, startUpperLayer, stopUpperLayer } from "./animation";
import { packBodyHitsUniform } from "./voxel";
import type { SnapshotBodyHit } from "../net/protocol";
import { applyServerBodyVox } from "./bodyVoxMerge";
import { getHeadNeckSkinJointIndices } from "./bodyVoxLimbs";

/** Максимальный HP NPC в wire-протоколе. */
export const NPC_MAX_HP = 100;

/** Радиус цилиндра-hitbox'а (XY, м). Зеркалирует сервер NpcRadius/
 *  также используется для рейкаста по обычным игрокам. */
export const ACTOR_RADIUS = 0.35;
/** Высота цилиндра (Z, м). Зеркалирует NpcHeight / игрок. */
export const ACTOR_HEIGHT = 1.8;
/** Если с момента последнего хита прошло больше — выходим из hurt-overlay. */
export const HURT_IDLE_TIMEOUT_MS = 120;

/** Server state codes (совпадают с proto.go NpcState*). */
export const NPC_STATE_ALIVE = 0;
export const NPC_STATE_DYING_FRONT = 1;
export const NPC_STATE_DYING_BACK = 2;

export interface NpcSpawnParams {
  x: number;
  y: number;
  /** Высота ступней в мире (м); v19+ с сервера, иначе 0 (плоский пол). */
  feetZ?: number;
  yaw: number;
  yawOffset: number;
  scale: number;
  floorOffset: number;
  /** Опционально: воксельное тело (тот же скелет что у основного mesh). */
  innerMesh?: LoadedMesh | null;
  /** Режим soldier_animated_real_vox.glb (отдельный draw на куб). */
  realVoxelBody?: boolean;
}

export class Npc {
  readonly inst: MeshInstance;
  readonly floorOffset: number;
  hp: number = NPC_MAX_HP;
  state: number = NPC_STATE_ALIVE;
  /** Флинч-триггер storage. */
  lastHitAt: number = -Infinity;
  /** Target state от сервера — применяется lerp'ом в tick(). */
  private targetX: number;
  private targetY: number;
  private targetFeetZ: number;
  private targetYaw: number;
  /** Экспоненциально сглаженная горизонтальная скорость для baseAnim
   *  (idle/run). Накапливается между snapshot'ами (computeSpeedBetweenSnapshots). */
  private smoothedSpeed: number = 0;
  private lastServerX: number;
  private lastServerY: number;
  private lastServerAt: number = 0;

  constructor(renderer: Renderer, mesh: LoadedMesh, params: NpcSpawnParams) {
    this.floorOffset = params.floorOffset;
    const fz = params.feetZ ?? 0;
    this.targetX = params.x;
    this.targetY = params.y;
    this.targetFeetZ = fz;
    this.targetYaw = params.yaw;
    this.lastServerX = params.x;
    this.lastServerY = params.y;
    this.lastServerAt = 0;
    this.inst = renderer.addMeshInstance(renderer.createMeshInstance({
      mesh,
      innerMesh: params.innerMesh ?? null,
      realVoxelBody: params.realVoxelBody === true,
      // Без carve по каркасу: внутренний воксельный слой без «дыр» (только meatTint + дербис по shell).
      innerBodyVoxShaderCarve: false,
      x: params.x, y: params.y, z: params.floorOffset + fz,
      yaw: params.yaw, yawOffset: params.yawOffset,
      scale: params.scale,
      initialAnim: "idle",
    }));
    this.inst.headNeckBoneIndices = getHeadNeckSkinJointIndices();
  }

  /** Вызывается при каждом snapshot'e от сервера. Сохраняет target позицию,
   *  триггерит flinch/death transition по дельте. Сам mesh двигается в tick(). */
  onServerState(
    x: number, y: number, feetZ: number, yaw: number,
    hp: number, state: number, hits: SnapshotBodyHit[], nowMs: number,
    bodyVox: Uint8Array,
    bodyVoxShell: Uint8Array,
  ): boolean {
    this.targetX = x;
    this.targetY = y;
    this.targetFeetZ = feetZ;
    this.targetYaw = yaw;

    // Скорость по дельте позиции между snapshot'ами.
    const dtServer = Math.max(1e-3, (nowMs - this.lastServerAt) / 1000);
    if (this.lastServerAt > 0) {
      const instSpeed = Math.hypot(x - this.lastServerX, y - this.lastServerY)
        / dtServer;
      this.smoothedSpeed = this.smoothedSpeed
        + (instSpeed - this.smoothedSpeed) * 0.4;
    }
    this.lastServerX = x;
    this.lastServerY = y;
    this.lastServerAt = nowMs;

    // Flinch по дельте hp (только для живых).
    if (hp < this.hp && hp > 0 && state === NPC_STATE_ALIVE) {
      triggerHurtFlinch(this.inst.state, this.inst.mesh);
      this.lastHitAt = nowMs;
    }
    // Death / respawn transitions.
    if (state !== this.state) {
      if (state === NPC_STATE_DYING_FRONT) {
        stopUpperLayer(this.inst.state);
        const clip = this.inst.mesh.animations.has("die") ? "die" : "idle";
        setBaseAnim(this.inst.state, clip);
      } else if (state === NPC_STATE_DYING_BACK) {
        stopUpperLayer(this.inst.state);
        const clip = this.inst.mesh.animations.has("dieBack")
          ? "dieBack"
          : (this.inst.mesh.animations.has("die") ? "die" : "idle");
        setBaseAnim(this.inst.state, clip);
      } else if (state === NPC_STATE_ALIVE) {
        setBaseAnim(this.inst.state, "idle");
        // Respawn — телепортируемся в новую позицию без lerp'а (иначе
        // «летим» через всю арену к spawn-point'у).
        this.inst.x = x;
        this.inst.y = y;
        this.inst.z = this.floorOffset + feetZ;
        this.inst.yaw = yaw;
      }
    }
    const respawnFullBody =
      this.state !== NPC_STATE_ALIVE && state === NPC_STATE_ALIVE;
    if (respawnFullBody) {
      this.inst.npcHeadshotDetach = false;
    }
    const shellStrip = applyServerBodyVox(this.inst, bodyVox, respawnFullBody, bodyVoxShell);

    this.hp = hp;
    this.state = state;
    this.inst.bodyHitCount = hits.length;
    this.inst.bodyHitsPacked = packBodyHitsUniform(hits);
    return shellStrip;
  }

  /** Тик pose + lerp инстанса к target* + авто-переключение baseAnim (idle/run)
   *  + выход из hurt-overlay по таймеру. */
  tick(dtSec: number, renderer: Renderer, nowMs: number): void {
    // Lerp к server-target. τ=80мс — плавно, но не залипает.
    const alpha = 1 - Math.exp(-dtSec / 0.08);
    this.inst.x += (this.targetX - this.inst.x) * alpha;
    this.inst.y += (this.targetY - this.inst.y) * alpha;
    const tz = this.floorOffset + this.targetFeetZ;
    this.inst.z += (tz - this.inst.z) * alpha;
    const dy = wrapAngle(this.targetYaw - this.inst.yaw);
    this.inst.yaw = wrapAngle(this.inst.yaw + dy * alpha);

    // baseAnim idle/run по сглаженной speed — ТОЛЬКО если NPC жив.
    if (this.state === NPC_STATE_ALIVE) {
      const hasRun = this.inst.mesh.animations.has("run");
      const target = hasRun && this.smoothedSpeed > 0.5 ? "run" : "idle";
      const cur = this.inst.state.baseAnim;
      if (cur !== "die" && cur !== "dieBack" && cur !== target) {
        setBaseAnim(this.inst.state, target);
      }
    }

    renderer.updateMeshInstancePose(this.inst, dtSec);
    stopHurtFlinchIfDone(this.inst.state, this.inst.mesh, this.lastHitAt, nowMs);
  }

  /** Trigger shoot-overlay (вызывается из main.ts при EvNpcShot). Короткий
   *  firingRifle поверх idle/run. Shoot не блокирует hurt (hurt имеет
   *  приоритет). Снимается через поставленный timeout в main.ts. */
  triggerShoot(): void {
    const u = this.inst.state.upperLayer;
    if (u !== null && u.clipName === "hurt") return; // hurt в приоритете
    if (this.inst.mesh.animations.has("shoot")) {
      startUpperLayer(this.inst.state, "shoot", true, 2.0);
    }
  }
  stopShoot(): void {
    const u = this.inst.state.upperLayer;
    if (u !== null && u.clipName === "shoot" && u.weight > 0) {
      stopUpperLayer(this.inst.state);
    }
  }

  hpFraction(): number {
    return Math.max(0, Math.min(1, this.hp / NPC_MAX_HP));
  }

  /** Горизонтальная скорость (м/с, сглаженная) для клиентских SFX шагов. */
  footstepHorizSpeed(): number {
    return this.state === NPC_STATE_ALIVE ? this.smoothedSpeed : 0;
  }

  /** Y-координата HP-бара: чуть выше макушки. */
  headAnchor(): [number, number, number] {
    const feet = this.inst.z - this.floorOffset;
    return [this.inst.x, this.inst.y, feet + ACTOR_HEIGHT + 0.25];
  }

  /** Цилиндрический hit-test (используется только для hover HP-бара,
   *  не для реального hit'а — тот делает сервер). */
  intersectRay(
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
    maxT: number = Infinity,
  ): number | null {
    const feetZ = this.inst.z - this.floorOffset;
    return intersectCylinder(
      this.inst.x, this.inst.y, feetZ, ACTOR_RADIUS, ACTOR_HEIGHT,
      ox, oy, oz, dx, dy, dz, maxT,
    );
  }
}

// ═══════════════════════════════════════════════════════════════════════
// Общие helpers (используются и NPC-классом, и main.ts для игроков).
// ═══════════════════════════════════════════════════════════════════════

export function triggerHurtFlinch(state: AnimState, mesh: LoadedMesh): boolean {
  if (!mesh.animations.has("hurt") || !mesh.hurtMask) return false;
  const cur = state.upperLayer;
  const isHurt = cur !== null && cur.clipName === "hurt" && cur.weight >= 0;
  if (isHurt && cur) {
    cur.time = 0;
  } else {
    startUpperLayer(state, "hurt", /*looping=*/false, /*speed=*/1.0,
      mesh.hurtMask, /*extraNodes=*/[]);
  }
  return true;
}

export function stopHurtFlinchIfDone(
  state: AnimState, mesh: LoadedMesh, lastHitAt: number, now: number,
): void {
  const u = state.upperLayer;
  if (!u || u.clipName !== "hurt" || u.weight <= 0) return;
  const clip = mesh.animations.get("hurt");
  const finished = clip !== undefined && clip.duration > 0 &&
    u.time >= clip.duration;
  const stale = now - lastHitAt > HURT_IDLE_TIMEOUT_MS;
  if (finished || stale) stopUpperLayer(state);
}

export function intersectCylinder(
  cx: number, cy: number, cz: number,
  radius: number, height: number,
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  maxT: number = Infinity,
): number | null {
  const exX = ox - cx;
  const exY = oy - cy;
  const A = dx * dx + dy * dy;
  const B = 2 * (exX * dx + exY * dy);
  const C = exX * exX + exY * exY - radius * radius;

  let tEnterXY = 0, tExitXY = maxT;
  if (A < 1e-6) {
    if (C > 0) return null;
  } else {
    const disc = B * B - 4 * A * C;
    if (disc < 0) return null;
    const sq = Math.sqrt(disc);
    tEnterXY = (-B - sq) / (2 * A);
    tExitXY = (-B + sq) / (2 * A);
  }

  let tEnterZ = 0, tExitZ = maxT;
  if (Math.abs(dz) < 1e-6) {
    if (oz < cz || oz > cz + height) return null;
  } else {
    const t0 = (cz - oz) / dz;
    const t1 = (cz + height - oz) / dz;
    tEnterZ = Math.min(t0, t1);
    tExitZ = Math.max(t0, t1);
  }

  const tEnter = Math.max(tEnterXY, tEnterZ, 0);
  const tExit = Math.min(tExitXY, tExitZ, maxT);
  if (tEnter > tExit) return null;
  return tEnter;
}

function wrapAngle(a: number): number {
  while (a > Math.PI) a -= 2 * Math.PI;
  while (a < -Math.PI) a += 2 * Math.PI;
  return a;
}
